/**
 * Request and response schemas for the authentication surface: signup, login, logout, session
 * switch, and `me`.
 *
 * These live in `shared/` rather than beside the routes because a Zod schema here is the single
 * source of truth for both halves of the wire — the server validates against it at the boundary and
 * the web app infers its own types from the same object, so the two cannot drift
 * (`docs/roadmap.md` §1, "Zod-as-single-source-of-truth"). A schema duplicated in the client is a
 * client that keeps accepting a field the server has started rejecting.
 *
 * Three properties are deliberate and each is load-bearing:
 *
 *   1. **Every object schema is strict.** An unrecognised key is a rejection, not a silently
 *      ignored extra. Mass assignment is the specific attack: a permissive object schema is how
 *      `{"email": ..., "password": ..., "role": "physician"}` reaches a handler that spreads the
 *      parsed value into an insert. Nothing here is spread into an insert today, and the schema
 *      still refuses, because "nothing spreads it today" is not a property a future edit preserves.
 *   2. **No server-owned value appears in any request schema.** No `orgId` on signup, no `userId`
 *      anywhere, no role, no tier, no timestamp — `claude-kit/rules/invariants.md`,
 *      "Server-authoritative values never come from the request". `loginRequestSchema` does carry an
 *      optional `orgId`, and that is not an exception: it is a *choice among the caller's own
 *      memberships*, checked server-side against `org_members` before it is used for anything, and
 *      a value that fails that check is a denial rather than a scope.
 *   3. **The password rules are here, not in `passwords.ts`.** That module states plainly that it
 *      holds no policy and will hash an empty string if asked, so that half of one rule does not end
 *      up in two places. This is the other half.
 */

import { z } from 'zod';

/** RFC 5321's maximum reverse-path length. Longer than any real address and a cheap upper bound. */
export const MAX_EMAIL_LENGTH = 254;

/**
 * Minimum password length.
 *
 * NIST SP 800-63B's floor is 8 with a recommendation of 15 for user-chosen secrets; 12 is the
 * compromise this project starts at for staff accounts protecting PHI, and raising it later is a
 * one-line change that costs nothing to existing users (the check runs on the way in, not against
 * stored hashes).
 *
 * What is deliberately **absent** is a composition rule — no required digit, no required symbol,
 * no character-class count. The same NIST guidance advises against them: they measurably push people
 * toward `Password1!` and toward writing the result on the monitor of a shared clinical workstation,
 * which is the threat this whole subsystem exists to reduce. Length plus a breach-list check
 * (`BACKLOG.md` A6) is the pairing that actually works, and the breach list is the half still to
 * build.
 */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * Upper bound, so a caller cannot hand the hashing path an unbounded string.
 *
 * scrypt's cost is dominated by N and r rather than by input length, so this is not a defence
 * against an expensive derivation — it is a defence against a multi-megabyte body being copied
 * through the request pipeline once per login attempt. 256 is far above any real passphrase and
 * satisfies NIST's "accept at least 64 characters" requirement with room to spare.
 */
export const MAX_PASSWORD_LENGTH = 256;

export const MIN_PRACTICE_NAME_LENGTH = 2;
export const MAX_PRACTICE_NAME_LENGTH = 200;

/**
 * An email address, normalised to a trimmed lowercase string before it is validated.
 *
 * The order matters and is why this is a `.pipe()` rather than a chain of checks: Zod runs checks in
 * the order they were added, so `z.email().trim()` validates the *untrimmed* value and rejects a
 * pasted address with a trailing space — measured against zod 4.4.3, not assumed. Normalising first
 * and validating the normalised result is also what makes the stored value match what
 * `users_email_unique_idx` (a unique index on `lower(email)`) will compare against, so two accounts
 * differing only in case cannot be created.
 */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email({ error: 'Enter a valid email address' }).max(MAX_EMAIL_LENGTH));

/**
 * A password, taken **exactly as sent** — no trim, no case folding, no Unicode normalisation.
 *
 * Trimming a password is a silent correctness bug rather than a convenience: a passphrase whose
 * author ended it with a space would be hashed one way at signup and another way at login if either
 * side ever stopped trimming, and the user is locked out of their own account with nothing in any
 * log to explain it. `passwords.ts` makes the same commitment about normalisation for the same
 * reason.
 */
export const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `Use at least ${MIN_PASSWORD_LENGTH} characters`)
  .max(MAX_PASSWORD_LENGTH, `Use at most ${MAX_PASSWORD_LENGTH} characters`);

/** The practice's own name. Trimmed, because a leading space in a tenant name is a typo, not data. */
export const practiceNameSchema = z
  .string()
  .trim()
  .min(MIN_PRACTICE_NAME_LENGTH, 'Enter the practice name')
  .max(MAX_PRACTICE_NAME_LENGTH, `Use at most ${MAX_PRACTICE_NAME_LENGTH} characters`);

/**
 * Self-signup: create a practice and its first administrator in one call.
 *
 * No `role` field, and that is not an omission. The first member of a new practice is its
 * `practice_admin` by construction, decided server-side — a role accepted from an unauthenticated
 * request body is a privilege-escalation surface with no compensating check anywhere behind it.
 * Note also which role that is: `practice_admin` holds no clinical capability at all
 * (`authorization.ts`), so signing up does not hand anybody a chart.
 */
export const signupRequestSchema = z.strictObject({
  practiceName: practiceNameSchema,
  email: emailSchema,
  password: passwordSchema,
});
export type SignupRequest = z.infer<typeof signupRequestSchema>;

/** Six decimal digits — the wire format every TOTP authenticator app produces. */
export const totpCodeSchema = z
  .string()
  .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app');

/**
 * A recovery code as typed back in. Loosely bounded rather than matched against the exact generated
 * shape (`auth/recoveryCodes.ts`) — the server compares by hash regardless of formatting, so this
 * only needs to stop an absurdly long string from reaching that comparison.
 */
export const recoveryCodeSchema = z.string().trim().min(1).max(64);

/**
 * The second factor of `loginRequestSchema`, below. A client that gets back `mfa_required` resubmits
 * the same login with one of these two fields added — the same shape this file's own `orgId` field
 * already uses, a choice supplied only *after* the caller has proven their password, rather than a
 * second, stateful "challenge token" mechanism for what is structurally the same
 * resubmit-with-more-information pattern.
 *
 * Exactly one of the two should be present; the server tries `totpCode` first when both are (Zod
 * cannot express "at most one of" without `.refine`, and a caller sending both is confused rather
 * than malicious, so refusing outright would not help them).
 */
const mfaSecondFactorFields = {
  totpCode: totpCodeSchema.optional(),
  recoveryCode: recoveryCodeSchema.optional(),
};

/**
 * Login. `orgId` selects which practice to open the session against when the person belongs to more
 * than one — a clinician working across practices is the ordinary case here, not an edge case
 * (`BACKLOG.md` T8).
 *
 * Omitting it is the common path and means "the one practice I belong to". When the caller belongs
 * to several and names none, the server answers with the list and no session, rather than guessing;
 * guessing would silently open a session in the wrong tenant, which is the scope every subsequent
 * query in that session inherits.
 */
export const loginRequestSchema = z.strictObject({
  email: emailSchema,
  password: passwordSchema,
  orgId: z.uuid().optional(),
  ...mfaSecondFactorFields,
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

/** Move an existing authenticated identity to another practice they are a member of. */
export const switchOrgRequestSchema = z.strictObject({
  orgId: z.uuid(),
});
export type SwitchOrgRequest = z.infer<typeof switchOrgRequestSchema>;

// --- Email verification, password reset, TOTP MFA (`BACKLOG.md` A6) --------------------------------

/**
 * A single-use, emailed token as the client hands it back — opaque here, whatever length the server
 * issued. No format constraint beyond non-empty: the server-side lookup is what decides validity
 * (a sha256 comparison), so a schema-level length/charset check would only add a second, redundant
 * place that check could drift from the real one.
 */
const verificationTokenFieldSchema = z.string().min(1).max(512);

/** (Re)send a verification email for the caller's own address. Authenticated — no email in the body. */
export const verifyEmailRequestRequestSchema = z.strictObject({});
export type VerifyEmailRequestRequest = z.infer<typeof verifyEmailRequestRequestSchema>;

/** Redeem an emailed verification token. Public — the token itself is the proof of identity. */
export const verifyEmailConfirmRequestSchema = z.strictObject({
  token: verificationTokenFieldSchema,
});
export type VerifyEmailConfirmRequest = z.infer<typeof verifyEmailConfirmRequestSchema>;

/**
 * Ask for a password-reset email. Public, and deliberately answered identically regardless of
 * whether `email` has an account — see `auth/service.ts`'s `login()` header comment for why a
 * clinical system cannot let this become an account-existence oracle.
 */
export const passwordResetRequestRequestSchema = z.strictObject({
  email: emailSchema,
});
export type PasswordResetRequestRequest = z.infer<typeof passwordResetRequestRequestSchema>;

/** Redeem a password-reset token and set a new password. Public — the token is the proof. */
export const passwordResetConfirmRequestSchema = z.strictObject({
  token: verificationTokenFieldSchema,
  newPassword: passwordSchema,
});
export type PasswordResetConfirmRequest = z.infer<typeof passwordResetConfirmRequestSchema>;

/** Start TOTP enrollment. Authenticated, no body — the secret is generated for the caller's own id. */
export const mfaEnrollRequestSchema = z.strictObject({});
export type MfaEnrollRequest = z.infer<typeof mfaEnrollRequestSchema>;

/** Prove control of the authenticator before enrollment turns on. Authenticated. */
export const mfaEnrollConfirmRequestSchema = z.strictObject({
  code: totpCodeSchema,
});
export type MfaEnrollConfirmRequest = z.infer<typeof mfaEnrollConfirmRequestSchema>;

/**
 * The failure vocabulary of this surface, as a closed union so the web app can branch on it without
 * matching on prose.
 *
 * `invalid_credentials` is deliberately one code covering "no such account", "wrong password" and
 * "this account cannot sign in". Splitting it would turn login into an account-existence oracle,
 * which for a clinical system discloses something real before any password is ever guessed: that a
 * named person has an account here. `passwords.ts`'s decoy hash closes the same oracle in the timing
 * dimension; this closes it in the response.
 *
 * The codes that are *not* folded into it are the ones that describe the caller's own account and
 * are therefore not disclosure — a person who has already proven their password learns nothing from
 * `org_selection_required`.
 */
export const AUTH_ERROR_CODES = [
  /** The body failed schema validation. Carries no field detail beyond what Zod reports. */
  'invalid_request',
  /** No such account, wrong password, deactivated user, or no usable membership. One code. */
  'invalid_credentials',
  /** Correct credentials, but the caller belongs to several practices and named none. */
  'org_selection_required',
  /** Signup only: an account already exists for this address. */
  'email_taken',
  /** The caller is not a member of the practice they asked for. */
  'not_a_member',
  /** No session, or a session that has expired, been revoked, or belongs to a deactivated user. */
  'unauthenticated',
  /**
   * The audit trail could not be written, so the action did not happen. A write that cannot be
   * audited must not commit (`docs/roadmap.md` §3.2), so this is a refusal rather than a warning.
   */
  'audit_unavailable',
  /**
   * Login only: the password was correct, but the account has TOTP MFA enabled and no `totpCode` or
   * `recoveryCode` was supplied. Safe to disclose — unlike every code above it, this is returned only
   * *after* the caller has proven their password, so it tells them nothing they could not already
   * infer by having a correct password for their own account.
   */
  'mfa_required',
  /**
   * MFA enrollment confirm only: the submitted code did not verify against the pending secret.
   * Distinct from `invalid_credentials` because this endpoint is authenticated — the caller's
   * identity is already established, so there is no account-existence oracle to protect by folding
   * it into a shared code.
   */
  'invalid_totp_code',
  /** MFA enroll was requested while a confirmed enrollment already exists. Disable it first. */
  'mfa_already_enabled',
  /**
   * A password-reset or email-verification token was not found, already used, or past its expiry.
   * One code for all three, same reasoning as `invalid_credentials`: distinguishing "expired" from
   * "already used" from "never existed" would let a caller probe which is true for a token they did
   * not receive by email.
   */
  'invalid_or_expired_token',
  /**
   * Too many requests from this source in the current window (`BACKLOG.md` S6,
   * `server/src/security/rateLimiter.ts`). The response also carries a `Retry-After` header with the
   * number of seconds until the window resets — this code alone is not the whole contract, but it is
   * the part a schema can check.
   */
  'rate_limited',
] as const;
export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];

export function isAuthErrorCode(value: string): value is AuthErrorCode {
  return (AUTH_ERROR_CODES as readonly string[]).includes(value);
}

/** One practice the caller may open a session against. */
export const membershipSummarySchema = z.strictObject({
  orgId: z.uuid(),
  orgName: z.string(),
  role: z.string(),
});
export type MembershipSummary = z.infer<typeof membershipSummarySchema>;

/**
 * Who the caller is, in the practice this session is scoped to.
 *
 * `capabilities` is the resolved set for this identity, computed server-side by the same `decide()`
 * the server enforces with. The UI needs it to avoid offering an action that will be refused, and
 * deriving it from the chokepoint rather than from a parallel client-side table is what stops the
 * two disagreeing. It is a convenience for rendering and **never** an authorization decision: the
 * server re-decides on every request regardless of what the client believes it holds.
 */
export const authenticatedUserSchema = z.strictObject({
  userId: z.uuid(),
  email: z.string(),
  orgId: z.uuid(),
  orgName: z.string(),
  role: z.string(),
  /** Our own staff, resolved server-side from a platform grant table — never from a form or a URL. */
  isPlatformOperator: z.boolean(),
  tier: z.string(),
  capabilities: z.array(z.string()),
});
export type AuthenticatedUser = z.infer<typeof authenticatedUserSchema>;

/**
 * What signup, login and switch return on success.
 *
 * Note what is absent: the session token. It travels in a `Set-Cookie` header with `HttpOnly`, so it
 * is never readable by script and never lands in a response body a client might log, cache, or paste
 * into a bug report (`docs/roadmap.md` §7 invariant 8).
 */
export const authSessionResponseSchema = z.strictObject({
  user: authenticatedUserSchema,
});
export type AuthSessionResponse = z.infer<typeof authSessionResponseSchema>;

/** `GET /api/auth/me`. Same shape as a login response, plus every practice the caller could switch to. */
export const meResponseSchema = z.strictObject({
  user: authenticatedUserSchema,
  memberships: z.array(membershipSummarySchema),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/** The 409 body for `org_selection_required`: the caller's choices, and nothing else. */
export const orgSelectionResponseSchema = z.strictObject({
  error: z.literal('org_selection_required'),
  memberships: z.array(membershipSummarySchema),
});
export type OrgSelectionResponse = z.infer<typeof orgSelectionResponseSchema>;

/** The 409 body for `mfa_required`: nothing but the code — see its own comment for why that is safe. */
export const mfaRequiredResponseSchema = z.strictObject({
  error: z.literal('mfa_required'),
});
export type MfaRequiredResponse = z.infer<typeof mfaRequiredResponseSchema>;

/** Every other failure: a code, and never a reason that would distinguish two of them. */
export const authErrorResponseSchema = z.strictObject({
  error: z.enum(AUTH_ERROR_CODES),
});
export type AuthErrorResponse = z.infer<typeof authErrorResponseSchema>;

/**
 * A bare success acknowledgement, for every endpoint whose whole contract is "this either worked or
 * it didn't" — `password-reset/request` (deliberately no other detail, see that schema's own
 * comment), `verify-email/request`, `verify-email/confirm`, `password-reset/confirm`.
 */
export const okResponseSchema = z.strictObject({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof okResponseSchema>;

/**
 * What starting TOTP enrollment returns: enough for the client to render a QR code (`provisioningUri`,
 * an `otpauth://` URI) and a manual-entry fallback (`secret`, base32). Neither is a "credential" in
 * the sense the rest of this module protects — both are shown once, deliberately, to the person
 * enrolling their own second factor, the same way a recovery-code batch is shown once below.
 */
export const mfaEnrollResponseSchema = z.strictObject({
  secret: z.string(),
  provisioningUri: z.string(),
});
export type MfaEnrollResponse = z.infer<typeof mfaEnrollResponseSchema>;

/**
 * What confirming TOTP enrollment returns: the recovery-code batch, in the clear, exactly once. The
 * server stores only their hashes (`auth/recoveryCodes.ts`) — this response is the only moment the
 * plaintext codes exist outside the user's own record of them.
 */
export const mfaEnrollConfirmResponseSchema = z.strictObject({
  recoveryCodes: z.array(z.string()),
});
export type MfaEnrollConfirmResponse = z.infer<typeof mfaEnrollConfirmResponseSchema>;
